import React from 'react';
import I18n from 'i18n-js';
import PostalAddress from 'i18n-postal-address';
import { ChildProps, graphql } from 'react-apollo';
import { decamelizeKeys } from 'humps';
import gql from 'graphql-tag';
import { flowRight } from 'lodash';

import {
  core_apimessages_Address as IAddress,
  core_apimessages_Country as ICountry,
  ReverbAddressSummaryQuery,
} from '../../schemas/rql';
import { configFields } from '../../lib/address_form_fields_helpers';

interface IExternalProps {
  address: IAddress;
  externalCountries?: ICountry[];
}

type IProps = ChildProps<IExternalProps, ReverbAddressSummaryQuery.Query>;

const findCountry = (countryCode: string, countries: ICountry[]): ICountry => {
  return countries?.find(c => c.countryCode === countryCode);
};

const countryCodeToName = (countryCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  return country && country.name;
};

const regionNameInCountry = (countryCode: string, regionCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  const region = country && country.subregions.find(r => (r.code === regionCode));
  return region && region.name;
};

function isIncomplete(address: IAddress, configs) {
  const addressFields = configFields(address.countryCode, configs);
  const decamelizedAddress = decamelizeKeys(address);

  return addressFields?.some(field => !decamelizedAddress[field.fieldName] && field.required);
}

function getPostalAddress(address: IAddress, regionName) {
  const postalAddress = new PostalAddress();
  return postalAddress
    .setFirstName(address.name)
    .setAddress1(address.streetAddress)
    .setAddress2(address.extendedAddress)
    .setCity(address.locality)
    .setState(regionName)
    .setProvince(regionName)
    .setPostalCode(address.postalCode)
    .setFormat({
      country: address.countryCode,
    })
    .output();
}

function displayAddress(line, idx) {
  if (idx === 1) {
    return (
      <>
        <span className="formatted-address__highlight">
          {line}
        </span>
        <br />
      </>
    );
  }

  if (Array.isArray(line)) {
    return (
      <>
        <span>{line.join(' ')}</span>
        <br />
      </>
    );
  }

  return (
    <>
      <span>{line}</span>
      <br />
    </>
  );
}

function flattenPostalAddressLines(address) {
  return address.map(line => {
    if (line.length > 1) {
      return line;
    }

    return line[0];
  });
}

export function AddressSummary({ address, data, externalCountries }: IProps) {
  if (data.loading) return null;

  const countries = externalCountries || data?.countries?.countries;
  const configs = data?.intlAddressFormConfig?.configs;
  const countryName = countryCodeToName(address.countryCode, countries);
  const regionName = regionNameInCountry(address.countryCode, address.region, countries);
  const postalAddressDisplay = flattenPostalAddressLines(getPostalAddress(address, regionName));

  return (
    <div className="formatted-address">
      <p className="formatted-address__info">
        {postalAddressDisplay.map(displayAddress)}
        {countryName}
      </p>
      {isIncomplete(address, configs) &&
        <p className="formatted-address__incomplete">
          {I18n.t('discovery.addressBook.addressSummary.incompleteAddress')}
        </p>
      }
    </div>
  );
}

const ADDRESS_SUMMARY_QUERY = gql`
  query Reverb_Address_Summary_Query(
    $externalCountries: Boolean!
    ) {
    countries @skip (if: $externalCountries) {
      countries {
        name
        countryCode
        subregions {
          name
          code
        }
      }
    }
    intlAddressFormConfig {
      configs {
        countryCode
        fields {
          fieldName
          displayNames {
            locale
            displayName
          }
          required
        }
      }
    }
  }
`;

const connect = graphql<IExternalProps, ReverbAddressSummaryQuery.Query>(
  ADDRESS_SUMMARY_QUERY,
  {
    options: ({ externalCountries }) => {
      return {
        variables: {
          externalCountries: externalCountries?.length > 0,
        },
      };
    },
  },
);

export default flowRight([connect])(AddressSummary);
